import { T, Var } from 'gt-react';
import { Button } from 'src/components/ui/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import type { DraftConflictPlan } from './agentDefinitionEditActions';

export function AgentDraftConflictDialog({
  plan,
  pending,
  onDismiss,
  onResume,
  onReplace,
}: {
  plan: DraftConflictPlan | null;
  pending: boolean;
  onDismiss: () => void;
  onResume: () => void;
  onReplace: () => void;
}) {
  return (
    <AlertDialog
      open={conflictOpen(plan)}
      onOpenChange={(open) => dismissOnClose(open, onDismiss)}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="dialog title when edit would open an existing agent draft">
              You already have a draft
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <ConflictDescription plan={plan} />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            <T context="button, cancel choosing an agent draft">Cancel</T>
          </AlertDialogCancel>
          <Button variant="outline" disabled={pending} onClick={onResume}>
            <T context="button, resume the existing agent draft">
              Resume draft
            </T>
          </Button>
          <Button variant="destructive" disabled={pending} onClick={onReplace}>
            <T context="button, delete the existing agent draft and edit the version on screen">
              Delete draft and edit this version
            </T>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function conflictOpen(plan: DraftConflictPlan | null): boolean {
  return plan !== null;
}

function dismissOnClose(open: boolean, onDismiss: () => void): void {
  if (open) return;
  onDismiss();
}

function ConflictDescription({ plan }: { plan: DraftConflictPlan | null }) {
  if (!plan) return null;
  return <ConflictDraftDescription plan={plan} />;
}

function ConflictDraftDescription({ plan }: { plan: DraftConflictPlan }) {
  if (plan.copiedFromViewedVersion) {
    return <ConflictCopiedFromScreen plan={plan} />;
  }
  if (plan.parentVersion === null) return <ConflictUnknownParent plan={plan} />;
  return <ConflictCopiedFromOther plan={plan} />;
}

function ConflictCopiedFromScreen({ plan }: { plan: DraftConflictPlan }) {
  return (
    <T context="dialog body when the existing agent draft was copied from the version on screen">
      Draft v<Var>{plan.draftVersion}</Var> was already copied from this version
      (v<Var>{plan.viewedVersion}</Var>). It may contain edits that are not on
      this screen. Resume opens those edits. Delete and edit this version throws
      those edits away and starts a fresh copy. Neither choice changes which
      version new runs use.
    </T>
  );
}

function ConflictUnknownParent({ plan }: { plan: DraftConflictPlan }) {
  return (
    <T context="dialog body when an agent draft has no parent version on record">
      Draft v<Var>{plan.draftVersion}</Var> is not tied to a version still in
      the history. <ViewedVersionSentence plan={plan} /> Resume opens that
      draft. Delete and edit this version permanently removes it and copies v
      <Var>{plan.viewedVersion}</Var> into a new draft. Neither choice changes
      which version new runs use.
    </T>
  );
}

function ConflictCopiedFromOther({ plan }: { plan: DraftConflictPlan }) {
  return (
    <T context="dialog body when an agent draft was copied from a different version">
      Draft v<Var>{plan.draftVersion}</Var> was copied from v
      <Var>{plan.parentVersion}</Var>. <ViewedVersionSentence plan={plan} />{' '}
      Resume opens that draft. Delete and edit this version permanently removes
      draft v<Var>{plan.draftVersion}</Var>, including anything changed in it,
      and copies v<Var>{plan.viewedVersion}</Var> into a new draft. Neither
      choice changes which version new runs use.
    </T>
  );
}

function ViewedVersionSentence({ plan }: { plan: DraftConflictPlan }) {
  if (plan.viewedIsPublished) {
    return (
      <T context="sentence naming the published agent version on screen">
        This screen is showing published v<Var>{plan.viewedVersion}</Var>.
      </T>
    );
  }
  return (
    <T context="sentence naming the agent version on screen">
      This screen is showing v<Var>{plan.viewedVersion}</Var>.
    </T>
  );
}
